/**
 * PDS-based Icon Components
 *
 * SVG icons using PDS design tokens for consistent styling.
 * Icons are 16x16px by default with neutral-400 color.
 */

import React from 'react';

interface IconProps {
  size?: number;
  color?: string;
  className?: string;
}

export const MoreVerticalIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="3" r="1.5" fill={color} />
    <circle cx="8" cy="8" r="1.5" fill={color} />
    <circle cx="8" cy="13" r="1.5" fill={color} />
  </svg>
);

export const CheckIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.5 4.5L6 12L2.5 8.5"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RefreshIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C10.3643 2 12.3643 3.37857 13.2929 5.35714M13.2929 5.35714V2M13.2929 5.35714H10"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const JiraIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill={color}>
    <path d="M11.53,2a4.37,4.37,0,0,0,4.35,4.35h1.78v1.7A4.35,4.35,0,0,0,22,12.4V2.84A.85.85,0,0,0,21.16,2H11.53M6.77,6.8a4.36,4.36,0,0,0,4.34,4.34h1.8v1.72a4.36,4.36,0,0,0,4.34,4.34V7.63a.84.84,0,0,0-.83-.83H6.77M2,11.6a4.34,4.34,0,0,0,4.35,4.34H8.13v1.72A4.36,4.36,0,0,0,12.47,22V12.43a.85.85,0,0,0-.84-.84H2Z" />
  </svg>
);

export const SendIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 2L8 14M8 2L4 6M8 2L12 6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const CloseIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4L4 12M4 4L12 12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================================================
// Jira Integration Icons
// ============================================================================

export const SpinnerIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ animation: 'spin 1s linear infinite' }}
  >
    <circle
      cx="8"
      cy="8"
      r="6"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="28 10"
    />
  </svg>
);

export const SparkleIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 1L9.5 5.5L14 7L9.5 8.5L8 13L6.5 8.5L2 7L6.5 5.5L8 1Z"
      fill={color}
    />
    <path
      d="M12 10L12.75 12L15 12.75L12.75 13.5L12 15.5L11.25 13.5L9 12.75L11.25 12L12 10Z"
      fill={color}
    />
  </svg>
);

export const ExternalLinkIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 8.5V12.5C12 13.0523 11.5523 13.5 11 13.5H3.5C2.94772 13.5 2.5 13.0523 2.5 12.5V5C2.5 4.44772 2.94772 4 3.5 4H7.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 2.5H13.5V6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 9.5L13.5 2.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AlertTriangleIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.86 2.57L1.21 12.02C1.07 12.26 1 12.53 1 12.8C1 13.07 1.07 13.34 1.21 13.58C1.35 13.82 1.54 14.01 1.78 14.15C2.02 14.29 2.29 14.36 2.57 14.36H13.43C13.71 14.36 13.98 14.29 14.22 14.15C14.46 14.01 14.65 13.82 14.79 13.58C14.93 13.34 15 13.07 15 12.8C15 12.53 14.93 12.26 14.79 12.02L9.14 2.57C9 2.33 8.81 2.14 8.57 2C8.33 1.86 8.06 1.79 7.79 1.79C7.52 1.79 7.25 1.86 7.01 2C6.77 2.14 6.58 2.33 6.44 2.57H6.86Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 6V8.5"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="11" r="0.75" fill={color} />
  </svg>
);

export const CheckCircleIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke={color} strokeWidth="1.5" />
    <path
      d="M5.5 8L7 9.5L10.5 6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LinkIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 9.5C6.91 10.01 7.46 10.39 8.08 10.59C8.7 10.79 9.37 10.8 10 10.62C10.63 10.44 11.19 10.07 11.61 9.57C12.03 9.07 12.28 8.45 12.33 7.8L12.38 7.18C12.4 6.87 12.36 6.56 12.26 6.27C12.16 5.98 12 5.72 11.79 5.51L11.79 5.51C11.37 5.09 10.81 4.85 10.22 4.85C9.63 4.85 9.07 5.09 8.65 5.51L7.5 6.65"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 6.5C9.09 5.99 8.54 5.61 7.92 5.41C7.3 5.21 6.63 5.2 6 5.38C5.37 5.56 4.81 5.93 4.39 6.43C3.97 6.93 3.72 7.55 3.67 8.2L3.62 8.82C3.6 9.13 3.64 9.44 3.74 9.73C3.84 10.02 4 10.28 4.21 10.49L4.21 10.49C4.63 10.91 5.19 11.15 5.78 11.15C6.37 11.15 6.93 10.91 7.35 10.49L8.5 9.35"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronDownIcon: React.FC<IconProps> = ({ size = 16, color = '#6a6e7a' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6L8 10L12 6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
